import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { OutletTransition } from '@/shared/components/outlet-transition';
import { usePassbookQuery, PassbookProvider } from '@/shared/lib/passbook';
import { clearLastPassbookId, setLastPassbookId } from '@/shared/lib/last-passbook';
import { useAuth } from '@/shared/lib/auth';
import { withUserPageFlags } from '@/shared/lib/navigation';
import { FullPageLoader } from '@/shared/components/full-page-loader';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';
import { TenantAccessGuard } from './tenant-access-guard';

/** Provê contexto da loja para as telas em /loja/:idPassbook (shell fica no AppShell pai). */
export function TenantLayout() {
  const { idPassbook = '' } = useParams<{ idPassbook: string }>();
  const { user } = useAuth();
  const { data: passbook, isLoading, isError, refetch, isFetching } = usePassbookQuery(idPassbook);

  useEffect(() => {
    if (passbook && idPassbook && String(passbook._id) === idPassbook) {
      setLastPassbookId(idPassbook);
    }
  }, [passbook, idPassbook]);

  useEffect(() => {
    if (isError && idPassbook) clearLastPassbookId();
  }, [isError, idPassbook]);

  const showInitialLoader = isLoading && !passbook;
  const recoveryTo = user?.role === 'Admin' ? '/clientes' : '/selecionar-loja';

  if (showInitialLoader) return <FullPageLoader />;

  if (isError || !passbook) {
    return (
      <div className="grid min-h-[50dvh] place-items-center p-6">
        <div className="glass flex max-w-sm flex-col items-center gap-4 rounded-2xl p-8 text-center">
          <AlertTriangle className="size-10 text-warning" />
          <div>
            <h2 className="text-lg font-semibold">Loja não encontrada</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Esta loja não existe ou você não tem acesso. Escolha outra loja para continuar.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button asChild>
              <Link to={recoveryTo}>Escolher loja</Link>
            </Button>
            <Button variant="secondary" onClick={() => refetch()}>
              Tentar novamente
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PassbookProvider idPassbook={idPassbook} passbook={withUserPageFlags(passbook, user)}>
      <TenantAccessGuard>
        {isFetching && !showInitialLoader ? (
          <div className="mb-4 flex items-center gap-2 text-xs text-muted-foreground" aria-live="polite">
            <Skeleton className="h-3 w-24" />
            <span>Atualizando dados da loja…</span>
          </div>
        ) : null}
        <OutletTransition />
      </TenantAccessGuard>
    </PassbookProvider>
  );
}
